import { useState, type FormEvent } from 'react';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { patchMyTutorProfile } from '@/lib/tutor-api';
import { type TutorProfileResponse } from '@/lib/schemas/tutor-profile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function RateCard({
  profile,
  onSaved,
}: {
  profile: TutorProfileResponse;
  onSaved: (p: TutorProfileResponse) => void;
}) {
  const { accessToken } = useAuth();
  const [usd, setUsd] = useState(() =>
    profile.hourlyRate.usdMinor ? String(profile.hourlyRate.usdMinor / 100) : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const usdMinor = Math.round(Number.parseFloat(usd) * 100);
    if (!Number.isFinite(usdMinor) || usdMinor <= 0) {
      setError('Enter a valid hourly rate greater than zero.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const next = await patchMyTutorProfile(accessToken!, {
        hourlyRate: { usdMinor },
      });
      onSaved(next);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed — please retry.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Hourly Rate</CardTitle>
        <CardDescription>The rate parents see when browsing your profile.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={submit}>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          {success && <Alert variant="success"><AlertDescription>Saved — parents can see your updated rate.</AlertDescription></Alert>}

          <div className="space-y-1.5">
            <Label htmlFor="edit-rate-usd">Hourly rate (USD)</Label>
            <div className="relative max-w-xs">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">$</span>
              <Input
                id="edit-rate-usd"
                type="number"
                inputMode="decimal"
                step="0.01"
                min={1}
                value={usd}
                onChange={(e) => setUsd(e.target.value)}
                className="pl-7"
                placeholder="e.g. 7"
              />
            </div>
            <p className="text-xs text-muted-foreground">Rate shown to parents in US dollars.</p>
          </div>

          <div className="flex justify-end border-t border-slate-100 pt-4 dark:border-slate-700">
            <Button type="submit" disabled={busy} className="min-w-[140px]">
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
